import { gql, useFragment } from "@apollo/client";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";

// Styling
import style from "./header.module.scss";

// Constants
import { bgColorFields } from "@/constants/gql-fields";

// Import Components
import BackgroundColor from "@/components/bg-color/bg-color";
import Container from "../container/container";
import HeaderBanner from "../header--banner/header--banner";
import HeaderTopNav from "../header--topnav/header--topnav";
import HeaderColumns from "../header--columns/header--columns";
import HeaderMobileNav from "../header--mobile-nav/header--mobile-nav";
import SearchDropdown from "../header--search-dropdown/header--search-dropdown";

export default function Header({ entry }) {
  // Use Fragment
  const { complete, data } = useFragment({
    fragment: Header.fragments.entry,
    fragmentName: "HeaderFragment",
    from: {
      __typename: "RootQuery",
    },
  });

  if (!complete) {
    return;
  }

  // -- UseRouter
  const router = useRouter();

  // -- UseState
  const [navMobileState, setNavMobileState] = useState("closed");
  // - Search Dropdown State
  const [searchState, setSearchState] = useState(false);
  const searchClickState = { searchState, setSearchState };
  // useEffect -- reset mobile menu state on navigation
  useEffect(() => {
    if (navMobileState !== "closed") {
      const handleRouteChange = () => {
        setNavMobileState("closed");
        document.body.setAttribute("mobile-state", "closed");
      };
      router.events.on("routeChangeComplete", handleRouteChange);
      return () => {
        router.events.off("routeChangeComplete", handleRouteChange);
      };
    }
  }, [router]);

  // Variables
  const genSet = data.generalSettingsOptionsPage;
  const headerSettings = genSet.generalSettingsACF.headerSettings;
  const settingsContent = headerSettings.contentSettings ?? [];
  const settingsOptions = headerSettings.options ?? [];
  const settingsSpecial = headerSettings.specialOptions ?? [];

  var specialFormatted = {};
  settingsSpecial.forEach(function (item) {
    specialFormatted[item] = "";
  });

  return (
    <div
      className={`headerWrapper ${style.headerWrapper}`}
      {...specialFormatted}
      page={router.asPath}
    >
      {settingsContent.includes("banner") && (
        <HeaderBanner entry={genSet.headerBanner} />
      )}
      {settingsContent.includes("top-nav") && (
        <HeaderTopNav
          entry={genSet.topNav}
          menuData={data.topNav}
          searchClickState={searchClickState}
        />
      )}

      <header className={style.header}>
        {settingsOptions.includes("bgcolor") && (
          <BackgroundColor
            colorSettings={headerSettings?.headerBackgroundColorColorSettings}
          />
        )}
        <Container
          width={settingsOptions.includes("container--wide") && "wide"}
        >
          <HeaderColumns
            headerData={genSet.headerCols}
            navMobileState={navMobileState}
            setNavMobileState={setNavMobileState}
            searchClickState={searchClickState}
          />
        </Container>
      </header>

      {navMobileState == "open" && (
        <HeaderMobileNav
          data={genSet.mobileNav}
          navMobileState={navMobileState}
        />
      )}

      <SearchDropdown searchState={searchState} />
    </div>
  );
}

Header.fragments = {
  entry: gql`
    fragment HeaderFragment on RootQuery {
      # -- Menus
      ...HeaderMenus @unmask(mode: "migrate")
      ...TopNavMenu @unmask(mode: "migrate")
      ...MobileMenus @unmask(mode: "migrate")

      # -- General Settings
      generalSettingsOptionsPage {
        generalSettingsACF {
          id
          __typename
          headerSettings {
            __typename
            contentSettings
            options
            specialOptions
            headerBackgroundColorColorSettings {
             ${bgColorFields}
            }
          }
          headerContent {
            __typename
            bannerSettings {
              fieldGroupName
            }
            topNavSettings {
              fieldGroupName
            }
          }
        }
        topNav: generalSettingsACF {
          id
          __typename
          ...TopNavGenSet @unmask(mode: "migrate")
        }
        headerBanner: generalSettingsACF {
          id
          __typename
          ...HeaderBannerGenSet @unmask(mode: "migrate")
        }
        headerCols: generalSettingsACF {
          id
          __typename
          ...HeaderColGenSet @unmask(mode: "migrate")
        }
        mobileNav: generalSettingsACF {
          id
          __typename
          ...MobileNavFragment @unmask(mode: "migrate")
        }
      }
    }
    # Fragment Imports
    ${HeaderTopNav.fragments.entry}
    ${HeaderTopNav.fragments.menu}
    ${HeaderBanner.fragments.entry}
    ${HeaderColumns.fragments.menu}
    ${HeaderColumns.fragments.header}
    ${HeaderMobileNav.fragments.menus}
    ${HeaderMobileNav.fragments.entry}
  `,
};
